/**
 * API client for dashboard. Includes credentials for cookie auth.
 */

import { API_ROUTES } from '@worktracker/shared';

interface ApiResult<T> {
  success: boolean;
  data?: T;
  error?: { code: string; message: string };
}

async function request<T>(url: string, options: RequestInit = {}): Promise<ApiResult<T>> {
  const response = await fetch(url, {
    ...options,
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
    },
  });

  if (response.headers.get('Content-Type')?.includes('text/csv')) {
    const blob = await response.blob();
    const downloadUrl = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = downloadUrl;
    a.download = getFilenameFromHeader(response) ?? 'reports.csv';
    a.click();
    URL.revokeObjectURL(downloadUrl);
    return { success: true } as ApiResult<T>;
  }

  return response.json() as Promise<ApiResult<T>>;
}

export function apiGet<T>(url: string): Promise<ApiResult<T>> {
  return request<T>(url);
}

export function apiPost<T>(url: string, body?: unknown): Promise<ApiResult<T>> {
  return request<T>(url, {
    method: 'POST',
    body: body ? JSON.stringify(body) : undefined,
  });
}

export function apiPatch<T>(url: string, body?: unknown): Promise<ApiResult<T>> {
  return request<T>(url, {
    method: 'PATCH',
    body: body ? JSON.stringify(body) : undefined,
  });
}

export function apiDelete<T>(url: string): Promise<ApiResult<T>> {
  return request<T>(url, { method: 'DELETE' });
}

export function downloadCsv(params: URLSearchParams): void {
  const url = `${API_ROUTES.REPORTS_EXPORT}?${params.toString()}`;
  window.open(url, '_blank');
}

function getFilenameFromHeader(response: Response): string | null {
  const disposition = response.headers.get('Content-Disposition');
  if (!disposition) {
    return null;
  }
  const match = disposition.match(/filename="(.+)"/);
  return match?.[1] ?? null;
}
